import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Mail, Phone, MessageCircle, MapPin, Send, ArrowLeft, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Contact = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate sending (in production, this would call an API)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setLoading(false);
    setSubmitted(true);
    toast.success(language === 'it' ? 'Messaggio inviato!' : 'Message sent!');
  };

  const subjects = [
    { value: 'support', label: language === 'it' ? 'Assistenza generale' : 'General support' },
    { value: 'report', label: language === 'it' ? 'Segnala contenuto inappropriato' : 'Report inappropriate content' },
    { value: 'donation', label: language === 'it' ? 'Problemi con donazione' : 'Donation issues' },
    { value: 'account', label: language === 'it' ? 'Problemi con account' : 'Account issues' },
    { value: 'partnership', label: language === 'it' ? 'Partnership e collaborazioni' : 'Partnership and collaborations' },
    { value: 'advertising', label: language === 'it' ? 'Pubblicità e sponsorizzazioni' : 'Advertising and sponsorships' },
    { value: 'other', label: language === 'it' ? 'Altro' : 'Other' }
  ];

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {language === 'it' ? 'Torna alla home' : 'Back to home'}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-2">
            {language === 'it' ? 'Contattaci' : 'Contact Us'}
          </h1>
          <p className="text-stone-600 mb-8">
            {language === 'it' 
              ? 'Siamo qui per aiutarti. Scegli il modo migliore per contattarci.'
              : 'We\'re here to help. Choose the best way to contact us.'}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              {/* Email */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-emerald-800" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">
                  {language === 'it' ? 'Email Assistenza' : 'Support Email'}
                </h3>
                <a 
                  href="mailto:postmaster@moneyfor.it" 
                  className="text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  postmaster@moneyfor.it
                </a>
                <p className="text-sm text-stone-500 mt-2">
                  {language === 'it' ? 'Risposta entro 24-48 ore' : 'Response within 24-48 hours'}
                </p>
              </div>

              {/* Report */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <MessageCircle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">
                  {language === 'it' ? 'Segnala Abusi' : 'Report Abuse'}
                </h3>
                <a 
                  href="mailto:postmaster@moneyfor.it" 
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  postmaster@moneyfor.it
                </a>
                <p className="text-sm text-stone-500 mt-2">
                  {language === 'it' ? 'Per contenuti inappropriati o truffe' : 'For inappropriate content or scams'}
                </p>
              </div>

              {/* Business */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                  <Mail className="h-6 w-6 text-amber-700" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">
                  {language === 'it' ? 'Pubblicità e Partnership' : 'Advertising & Partnership'}
                </h3>
                <a 
                  href="mailto:postmaster@moneyfor.it" 
                  className="text-amber-700 hover:text-amber-800 font-medium"
                >
                  postmaster@moneyfor.it
                </a>
                <p className="text-sm text-stone-500 mt-2">
                  {language === 'it' ? 'Per aziende e collaborazioni' : 'For companies and collaborations'}
                </p>
              </div>

              {/* Hours */}
              <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="h-5 w-5 text-emerald-700" />
                  <h3 className="font-semibold text-emerald-900">
                    {language === 'it' ? 'Orari di Supporto' : 'Support Hours'}
                  </h3>
                </div>
                <p className="text-emerald-800 text-sm">
                  {language === 'it' ? 'Lun - Ven: 9:00 - 18:00' : 'Mon - Fri: 9:00 AM - 6:00 PM'}
                </p>
                <p className="text-emerald-800 text-sm">
                  {language === 'it' ? 'Sab - Dom: Solo emergenze' : 'Sat - Sun: Emergencies only'}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
                      {language === 'it' ? 'Messaggio Inviato!' : 'Message Sent!'}
                    </h3>
                    <p className="text-stone-600 mb-6">
                      {language === 'it' 
                        ? 'Ti risponderemo il prima possibile all\'indirizzo email fornito.'
                        : 'We will respond as soon as possible to the email address provided.'}
                    </p>
                    <Button onClick={() => setSubmitted(false)} variant="outline" className="rounded-xl">
                      {language === 'it' ? 'Invia un altro messaggio' : 'Send another message'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-xl font-semibold text-stone-900 mb-6">
                      {language === 'it' ? 'Inviaci un messaggio' : 'Send us a message'}
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">{language === 'it' ? 'Nome' : 'Name'}</Label>
                          <Input
                            id="name"
                            placeholder={language === 'it' ? 'Il tuo nome' : 'Your name'}
                            className="h-12 rounded-xl border-stone-200"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="nome@email.com"
                            className="h-12 rounded-xl border-stone-200"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{language === 'it' ? 'Oggetto' : 'Subject'}</Label>
                        <Select
                          value={formData.subject}
                          onValueChange={(value) => setFormData({ ...formData, subject: value })}
                        >
                          <SelectTrigger className="h-12 rounded-xl border-stone-200">
                            <SelectValue placeholder={language === 'it' ? 'Seleziona argomento' : 'Select topic'} />
                          </SelectTrigger>
                          <SelectContent>
                            {subjects.map(subject => (
                              <SelectItem key={subject.value} value={subject.value}>
                                {subject.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">{language === 'it' ? 'Messaggio' : 'Message'}</Label>
                        <Textarea
                          id="message"
                          placeholder={language === 'it' ? 'Scrivi il tuo messaggio...' : 'Write your message...'}
                          className="min-h-[150px] rounded-xl border-stone-200 resize-none"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            {language === 'it' ? 'Invia Messaggio' : 'Send Message'}
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
